import './Register.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import { useCookies } from 'react-cookie';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import YupPassword from 'yup-password';

import { PSButton } from '@/components/ui/button';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import HttpService from '../../app/http';
import { getToken } from '../../app/root';
import { useLoginMutation } from '../../app/services/auth-rtk.service';
import Alert, { IAlertType } from '../../components/common/Alert';
import BsInputPassword from '../../components/common/BsInputPassword';
import LanguageChangeDropdownShort from '../../components/common/LanguageChangeDropdownShort';
import SocialAuth from '../../components/common/SocialAuth';
import UILinkTo from '../../components/common/UILinkTo';
import { AlertsType } from '../../constants/alerts.constant';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import { getCurrencyPlanWithPlanCode } from '../../constants/plans.constant';
import { login as authSliceLogin, selectError, selectIsRememberMe, selectUserAuthStatus } from '../../features/auth/auth.slice';
import { setChosenPlan, setCurrency } from '../../features/dashboard.slice';
import useAppLocalStorage from '../../hooks/useAppLocalStorage';
import { setCookie } from '../../providers/CookieManagementProvider';
import { SumUserTypeEnum } from '../../utils/SumUserTypeEnum';
import Utils from '../../utils/utmManager';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';
import { Timer } from './ForgotPassword';
import PassWordValidationChecker from './PassWordValidationChecker';
import { RegisterInput } from './types';
import { withSocialAuth } from './withSocialAuth';
YupPassword(Yup);

const Register = (props: any) => {
  const { i18n, t } = useTranslation();
  const localStorage = useAppLocalStorage();
  const loginError = useAppSelector(selectError);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cookies] = useCookies(['utm_datas', '_fbc', '_fbp']);
  const isRememberMe = useAppSelector(selectIsRememberMe);
  const isAuthentication = useAppSelector(selectUserAuthStatus);

  const [loginUser, LoginUserMutation] = useLoginMutation();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [error, setError] = useState<any>();
  const [counter, setCounter] = useState(0);

  const [toast, setToast] = useState<IAlertType>({
    isShow: false,
    title: '',
    message: '',
    type: 'Error',
  });
  const [showConfirmEmailModal, setShowConfirmEmailModal] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [showTimer, setShowTimer] = useState(false);
  const [apiErrors, setApiErrors] = useState<any>({});
  const [toastConfirm, setToastConfirm] = useState<IAlertType>({
    isShow: false,
    title: '',
    message: '',
    type: 'Error',
  });

  const schema = Yup.object({
    user_email: Yup.string()
      .required(t('authPages/register/er-01'))
      .matches(/^[^\s@`!@#$%^&*()=]+@[^\s@`!@#$%^&*()=]+\.[^\s@`!@#$%^&*()=]+$/, t(AlertsType.ISATKO003))
      .email()
      .max(255)
      .label('Email'),
    user_password: Yup.string()
      .required(t('authPages/register/er-02'))
      .label('Password')
      .min(8, t(AlertsType.ISATKO004))
      .minLowercase(1, t(AlertsType.ISATKO005))
      .minUppercase(1, t(AlertsType.ISATKO006))
      .minSymbols(1, t(AlertsType.ISATKO007))
      .minNumbers(1, t(AlertsType.ISATKO008)),
    user_agree_terms: Yup.boolean()
      .required(t(AlertsType.ISATKO009))
      .typeError(t(AlertsType.ISATKO009))
      .label(t(AlertsType.ISATKO009))
      .isTrue(t(AlertsType.ISATKO009)),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterInput>({
    resolver: yupResolver(schema) as Resolver<RegisterInput, any>,
  });

  const rules = [
    {
      id: 1,
      regex: '^.{8,32}$',
      textTranslated: t(AlertsType.ISATKO004),
      text: 'Must be 8 to 32 character',
      isPassed: false,
    },
    {
      id: 2,
      regex: '(?=.*[a-z])',
      textTranslated: t(AlertsType.ISATKO005),
      text: 'At least one lower case letter exists',
      isPassed: false,
    },
    {
      id: 3,
      regex: '(?=.*[A-Z])',
      textTranslated: t(AlertsType.ISATKO006),
      text: 'At least one upper case letter exists',
      isPassed: false,
    },
    {
      id: 4,
      regex: '(?=.*\\d)',
      textTranslated: t(AlertsType.ISATKO008),
      text: 'At least one digit exists',
      isPassed: false,
    },
    {
      id: 5,
      regex: '(?=.*?[.#?!@$ %^&*-])',
      textTranslated: t(AlertsType.ISATKO007),
      text: 'At least one special character exists',
      isPassed: false,
    },
  ];

  const RegisterUserMutation = useMutation<any, AxiosError<any, any>, any>(data => HttpService.post('/auth/register', data), {
    onMutate: () => {
      setApiErrors({});
    },
  });

  const ResendEmailVerification = useMutation<any, AxiosError<any, any>>(() => HttpService.post(`/auth/${userData.userIdentification}/validation-link`), {
    onSuccess: () => {
      setShowTimer(true);
    },
    onError: () => {
      setShowTimer(false);
    },
  });

  const handleLogin = () => {
    const data: any = {
      user_identification: userData.userIdentification,
      user_email: userData.user_email,
      user_password: userData.user_password,
    };

    loginUser({
      user_email: data.user_email,
      user_password: data.user_password,
    })
      .unwrap()
      .then(({ token, refresh_token, userAccessToken, ...res }) => {
        localStorage.setItem(`LOUISEBOT_token`, token);
        localStorage.setItem(`LOUISEBOT_refresh_token`, refresh_token);
        localStorage.setItem(`LOUISEBOT_user_access_token`, userAccessToken);

        dispatch(
          authSliceLogin({
            is_remember_me: true,
          })
        );

        if (res?.plan == SumUserTypeEnum.BUSINESSPARTNER) {
          navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel/business-partner-panel`, {
            replace: true,
          });

          return;
        } else {
          navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`, {
            replace: true,
          });
        }
      })
      .catch(res => {
        if (res?.status && res?.status != 500) {
          let message = '';

          if (res?.data?.message && res?.data?.message == 'Credential error.') {
            message = t(AlertsType.ISATKO010);
          } else {
            message = res?.data?.message;
          }

          setToastConfirm({
            isShow: true,
            message: message || t(AlertsType.ISATKO010),
            title: 'Warning',
            type: 'warning',
          });
        }
      });
  };

  useEffect(() => {
    if (loginError) {
      setToast({
        isShow: true,
        message: loginError ? (loginError === 'Credential error.' ? t(AlertsType.ISATKO002) : loginError) : loginError,
        type: 'Error',
      });
    }
  }, [loginError]);

  const onSubmit = (data: any) => {
    handleReCaptchaVerify().then(recaptchaToken => {
      if (counter <= 0) {
        closeToast();

        const cloneData: {
          user_email: string;
          user_password: string;
          user_confirm_password: string;
          recaptcha_token?: string;
          user_utm_data?: any;
        } = {
          user_email: data.user_email,
          user_password: data.user_password,
          user_confirm_password: data.user_password,
          recaptcha_token: recaptchaToken,
        };

        if (window.navigator.cookieEnabled && cookies.utm_datas && cookies.utm_datas !== '') {
          cloneData.user_utm_data = cookies.utm_datas;

          cloneData.user_utm_data = {
            ...cloneData.user_utm_data,
            client_user_agent: navigator.userAgent,
          };

          if (cookies.utm_datas.fbclid) {
            if (cookies._fbp && cookies._fbp !== '') {
              cloneData.user_utm_data = {
                ...cloneData.user_utm_data,
                fbp: cookies._fbp,
              };
            }

            if (cookies._fbc && cookies._fbc !== '') {
              cloneData.user_utm_data = {
                ...cloneData.user_utm_data,
                fbc: cookies._fbc,
              };
            }
          }
        }

        RegisterUserMutation.mutate(cloneData, {
          onSuccess: res => {
            setShowConfirmEmailModal(true);
            setUserData({
              ...res.data.element,
              user_email: data.user_email,
              user_password: data.user_password,
            });
            setCounter(counter + 1);
            setError(null);
          },
          onError: res => {
            if (res?.response?.status && res?.response?.status != 500) {
              setToast({
                isShow: true,
                message: res?.response?.data?.message,
                title: 'Warning',
                type: 'warning',
              });
            }
          },
        });
      } else {
        setError({
          message: t(AlertsType.ISCUKO002),
        });
      }
    });
  };

  const closeToast = () => {
    setToast({
      isShow: false,
      message: '',
      title: '',
      type: 'warning',
    });
  };

  const closeToastConfirm = () => {
    setToastConfirm({
      isShow: false,
      message: '',
      title: '',
      type: 'warning',
    });
  };

  const handleClose = () => setShowConfirmEmailModal(false);

  useEffect(() => {
    Utils.utmManager(searchParams);

    const searchParamsData = searchParams.get('name');
    if (searchParamsData) {
      dispatch(setCurrency(getCurrencyPlanWithPlanCode(searchParamsData)));
      // const findPlan = Plans.find((dr) => dr.name === searchParamsData);
      // if (findPlan) {
      // dispatch(setChosenPlan(findPlan));
      dispatch(setChosenPlan(searchParamsData));
      // }
    }
  }, [searchParams]);

  useEffect(() => {
    const searchParamsData = searchParams.get('promocode');
    if (searchParamsData) {
      setCookie(`LOUISEBOT_promocode`, searchParamsData, 1);
    }
  }, [searchParams]);

  useEffect(() => {
    const persist = localStorage?.getItem(`persist:auth`);

    if (isRememberMe && isAuthentication && getToken() && persist) {
      navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`);
    }
  }, [isRememberMe, isAuthentication]);

  const handleReCaptchaVerify = useCallback(async (): Promise<string | undefined> => {
    try {
      if (!executeRecaptcha) {
        return undefined;
      }

      const token = await executeRecaptcha('register');

      return token;
    } catch (error) {
      return undefined;
    }
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  return (
    <div className="tw-bg-gray-100  tw-min-h-screen tw-h-full tw-flex tw-flex-col tw-justify-start tw-items-center md:tw-p-4  ">
      <div className="tw-text-center md:tw-h-16 tw-h-full tw-bg-gray-100 tw-my-2">
        <Link to="https://louisebot.com">
          <div className=" tw-justify-start tw-items-center tw-gap-1 tw-inline-flex">
            <img
              src="https://cdn.1cdn.app/application/LOUISEBOT/23091707385795_Logo_colorful.svg"
              className="tw-w-10 tw-h-10 tw-relative"
              alt="LouiseBot Logo"
            />
            <div className="tw-text-blue-700 tw-text-lg tw-font-extrabold tw-font-['Lexend'] tw-leading-7">LouiseBot</div>
          </div>
        </Link>
      </div>
      <div className="md:tw-px-24   md:tw-w-auto tw-w-full md:tw-pt-[126px] tw-p-7 tw-bg-white tw-rounded-2xl tw-flex-col tw-justify-between tw-items-center tw-inline-flex">
        <div className=" tw-justify-center tw-items-center tw-gap-24 tw-inline-flex">
          <div className=" tw-self-stretch tw-flex-col tw-justify-center tw-items-start tw-gap-12 md:tw-inline-flex  tw-hidden">
            <div className="tw-self-stretch tw-h-52 tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
              <div className="tw-self-stretch tw-text-gray-800 tw-text-4xl tw-font-bold tw-font-['Lexend'] tw-leading-10">{t('authPages/register/title')}</div>
              <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-normal tw-font-['Lexend'] tw-leading-9">
                • {t('authPages/register/subscription/benefits/benefit_one')}
                <br />• {t('authPages/register/subscription/benefits/benefit_two')}
                <br />• {t('authPages/register/subscription/benefits/benefit_three')}
                <br />• {t('authPages/register/subscription/benefits/benefit_four')}
              </div>
            </div>
          </div>
          <div className=" tw-flex-col md:tw-w-[448px] tw-w-full tw-justify-start tw-items-start tw-gap-8 tw-inline-flex">
            <div className="tw-w-full">
              <div className="md:tw-hidden tw-flex mb-4  justify-content-center align-items-center text-center tw-flex-col ">
                <h1 className=" register-title text-center font-sofiaSans w-100 tw-text-[30px]">{t('authPages/register/title')}</h1>
              </div>

              {props.isSocialAuthLoading ? (
                <div className="w-100 d-flex justify-content-center">
                  <Spinner variant="primary" animation="border" />
                </div>
              ) : (
                <SocialAuth
                  className="socialAuth"
                  providers={['Google']}
                  onLoginStartSocialAuthStart={props.onLoginStartSocialAuthStart}
                  onResolveSocialAuth={props.onResolveSocialAuth}
                  onRejectSocialAuth={props.onRejectSocialAuth}
                  text={{
                    Google: t('authPages/socialAuth/registerGoogle') as string,
                  }}
                />
              )}

              <div className="hor-line tw-mt-8 tw-mb-5">
                <p className="font-sofiaSans tw-text-xs tw-font-extrabold tw-text-zinc-500 tw-px-1 md:tw-w-auto tw-w-[80%] lg:tw-w-[65%] tw-text-center">
                  {t('authPages/socialAuth/l-02')}
                </p>
              </div>

              <Form onSubmit={handleSubmit(onSubmit)} className="register-form">
                {toast.isShow && (
                  <Alert type={toast.type} onClick={() => closeToast()}>
                    <h5 className="tw-font-bold">{toast.title}</h5>
                    <p className="tw-m-0">{toast.message}</p>
                  </Alert>
                )}
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="user_email" className="register-label">
                    {t('authPages/register/l-01')}
                  </Form.Label>
                  <Form.Control
                    id="user_email"
                    className="register-input"
                    type="email"
                    isInvalid={!!errors.user_email}
                    autoComplete="off"
                    {...register('user_email')}
                  />
                  <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                    {errors?.user_email?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <BsInputPassword
                  label={t('authPages/register/l-02')}
                  isInvalid={!!errors.user_password || !!apiErrors.user_password}
                  errorMessage={(errors.user_password?.type === 'required' && errors.user_password?.message) || apiErrors.user_password}
                  className={'mb-3'}
                  rhfControls={register('user_password')}
                  isPasswordStrengthBar={true}
                />

                {watch('user_password') && watch('user_password').length > 0 && (
                  <Form.Group className="mb-4 password-check">
                    <PassWordValidationChecker
                      password={watch('user_password')}
                      ruleSet={rules.map((rule: any) => {
                        return {
                          ...rule,
                          message: rule.textTranslated,
                        };
                      })}
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3 ">
                  <Form.Check
                    type="checkbox"
                    id="user_agree_terms"
                    label={
                      <small className="forgot-password ">
                        {t('authPages/register/l-03')} &nbsp;
                        <UILinkTo to="/terms-of-service" className="main-color register-bold-black text-decoration-underline" target="_blank">
                          {t('authPages/register/l-04')}
                        </UILinkTo>
                      </small>
                    }
                    isInvalid={!!errors.user_agree_terms}
                    {...register('user_agree_terms')}
                  />
                  <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                    {errors?.user_agree_terms?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <PSButton
                  id="btn-register-submit"
                  type="submit"
                  className="w-100 d-flex font-sofiaSans justify-content-center align-items-center"
                  size="lg"
                  disabled={RegisterUserMutation.isLoading || props.isSocialAuthLoading}
                  isLoading={RegisterUserMutation.isLoading}
                  title={t('authPages/register/btn-01')}
                />

                <p className="tw-text-[10px] tw-text-zinc-400 tw-text-center tw-mt-2">
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="tw-underline">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="tw-underline">
                    Terms of Service
                  </a>{' '}
                  apply.
                </p>

                <div className="register-create-link">
                  <small className="fs-5 color-forthColor d-flex gap-2 tw-whitespace-pre">
                    {t('authPages/register/l-05')}
                    <Link
                      to={`${window.location.origin}/${MultiLanguageUrl[i18n.language] || 'en-US'}/login`}
                      className="color-twelveColor fw-bold text-decoration-underline tw-whitespace-nowrap tw-whitespace-nowrap"
                    >
                      {t('authPages/register/l-06')}
                    </Link>
                  </small>
                </div>
              </Form>
            </div>
          </div>
        </div>
        <div className="md:tw-mt-[88px] tw-mt-[20px] tw-text-zinc-500 tw-text-[8px] tw-font-thin tw-font-['Roboto'] tw-leading-tight">
          © LouiseBot {new Date().getFullYear()} - {import.meta.env.VITE_IMAGE_VERSION}
        </div>
      </div>

      <div className="tw-bg-gray-100 tw-w-full tw-flex tw-justify-center tw-items-center tw-p-2">
        <LanguageChangeDropdownShort theme={'bg-eightColor'} />
      </div>

      {/* Confirm Email Modal*/}
      {showConfirmEmailModal && (
        <Modal show={showConfirmEmailModal} size="lg" centered onHide={handleClose}>
          <Modal.Header className="border-0 d-flex justify-content-center position-relative" closeButton />
          <Modal.Body className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-4">
            <div className="py-md-3">
              <svg width="280" height="280" viewBox="0 0 280 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="280" height="280" rx="140" fill="#F0F4FF" />
                <rect width="280" height="280" rx="140" fill="url(#paint0_radial_1788_7789)" />
                <path
                  d="M140 60C95.888 60 60 95.888 60 140C60 184.112 95.888 220 140 220C184.112 220 220 184.112 220 140C220 95.888 184.112 60 140 60ZM124.008 175.304L94.304 145.664L105.6 134.336L123.992 152.696L166.344 110.344L177.656 121.656L124.008 175.304Z"
                  fill="#4167DA"
                />
                <defs>
                  <radialGradient
                    id="paint0_radial_1788_7789"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(140 140) rotate(90) scale(140)"
                  >
                    <stop stopColor="#D9E2FB" stopOpacity="0" />
                    <stop offset="1" stopColor="#C8D6F8" stopOpacity="0.32" />
                  </radialGradient>
                </defs>
              </svg>
            </div>

            {toastConfirm.isShow && (
              <Alert className="tw-flex tw-items-center" type={toastConfirm.type} onClick={() => closeToastConfirm()}>
                {toastConfirm.message}
              </Alert>
            )}

            <h2 className="font-sofiaSans tw-font-bold ">{t('authPages/register/l-07')}</h2>

            <p className="font-poppins fs-5 tw-text-center">
              {t('authPages/register/text-01')} ({userData?.user_email}). <br />
              {t('authPages/register/text-02')}
            </p>

            <PSButton
              id="btn-register-login"
              className="justify-content-center align-items-center"
              onClick={handleLogin}
              disabled={LoginUserMutation.isLoading}
            >
              {LoginUserMutation.isLoading ? <Spinner size="sm" animation="border" /> : <span>{t('authPages/register/btn-02')}</span>}
            </PSButton>

            <PSButton
              id="btn-register-resend"
              variant="outline"
              onClick={() => userData.user_email && ResendEmailVerification.mutate(undefined)}
              disabled={showTimer}
              isLoading={ResendEmailVerification.isLoading}
              loadingAnimation="grow"
            >
              {showTimer ? (
                <span className="text-muted">
                  <Timer setShowTimer={setShowTimer} ttl={120} /> <>{t('authPages/register/l-08')}</>
                </span>
              ) : (
                <span>{t('authPages/register/btn-03')}</span>
              )}
            </PSButton>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

const RegisterWithRecaptcha = (props: any) => (
  <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_GOOGLE_RECAPTCHA as string}>
    <Register {...props} />
  </GoogleReCaptchaProvider>
);

export default WrappedErrorBoundary(withSocialAuth(RegisterWithRecaptcha));
